import React, { useEffect, useMemo, useState } from "react";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import api from "../lib/api";
import { DollarSign, Package, TrendingUp, ShoppingCart, Trophy, Users, Download, CalendarRange } from "lucide-react";
import DateRangePicker from "./DateRangePicker";
import { toast } from "sonner";
import { fmtMoney, fmtInt, fmtPct } from "../lib/format";

function StatCard({ label, value, icon: Icon, accent, testId }) {
  return (
    <div className={`brutal-card p-4 sm:p-5 ${accent ? "!bg-[#111] !text-white" : ""}`} data-testid={testId}>
      <div className="flex items-start justify-between">
        <div className="font-mono text-[10px] sm:text-[11px] uppercase tracking-widest opacity-80">{label}</div>
        <div className={`w-7 h-7 sm:w-8 sm:h-8 border-2 ${accent ? "border-white" : "border-black"} flex items-center justify-center flex-shrink-0`}>
          <Icon size={14}/>
        </div>
      </div>
      <div className="font-heading text-2xl sm:text-3xl font-black mt-2">{value}</div>
    </div>
  );
}

/**
 * Reusable analytics view used by Dashboard, Analytics page, and Event report.
 * Props:
 *  - eventId?: string         // when set, scope to a single event
 *  - title?: string           // section title
 *  - showDateFilter?: boolean // show date picker (default: true)
 *  - showExport?: boolean     // show CSV export button (default: true)
 *  - defaultRange?: "30d" | "all" | "today" | ...   // initial preset
 *  - heroLabel?: string       // override the highlighted hero stat label
 *  - children?: ReactNode     // extra content rendered after the analytics blocks
 */
export default function AnalyticsView({
  eventId,
  title = "Analytics",
  showDateFilter = true,
  showExport = true,
  defaultRange = "30d",
  heroLabel = "Revenue",
}) {
  const [range, setRange] = useState({ from: "", to: "", presetKey: defaultRange });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  const params = useMemo(() => {
    const p = new URLSearchParams();
    if (range.from) p.set("date_from", range.from);
    if (range.to) p.set("date_to", range.to);
    if (eventId) p.set("event_id", eventId);
    return p.toString();
  }, [range, eventId]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/analytics/summary${params ? `?${params}` : ""}`);
        if (!cancelled) setData(data);
      } catch (_) { /* offline / unauth — keep last */ }
      finally { if (!cancelled) setLoading(false); }
    };
    load();
    return () => { cancelled = true; };
  }, [params]);

  const downloadCsv = async () => {
    setExporting(true);
    try {
      const resp = await api.get(`/analytics/export/csv${params ? `?${params}` : ""}`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      const tag = `${range.from || "all"}_${range.to || "all"}`;
      a.download = `vendor-vault-sales-${tag}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("CSV downloaded");
    } catch (e) {
      toast.error("Could not export CSV");
    } finally { setExporting(false); }
  };

  const totals = data?.totals || { revenue: 0, profit: 0, units: 0, sales: 0, margin_pct: 0, cost: 0 };
  const daily = data?.daily || [];
  const topProducts = data?.top_products || [];
  const topSellers = data?.top_sellers || [];
  const byEvent = data?.by_event || [];

  return (
    <section className="space-y-6" data-testid="analytics-view">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <div className="font-mono text-[10px] sm:text-xs uppercase tracking-widest text-[#4A4A4A] flex items-center gap-2">
            <CalendarRange size={12}/> {title}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {showDateFilter && (
            <DateRangePicker
              value={range}
              onChange={(r) => setRange(r)}
              testIdPrefix="dashboard-range"
            />
          )}
          {showExport && (
            <button
              onClick={downloadCsv}
              disabled={exporting || !totals.sales}
              className="brutal-btn brutal-btn-secondary !py-2 !px-3 disabled:opacity-50"
              title={totals.sales ? "Export filtered sales as CSV" : "No sales in this range"}
              data-testid="analytics-export-csv"
            >
              <Download size={14}/>
              <span className="ml-2 hidden sm:inline">{exporting ? "Exporting…" : "CSV"}</span>
            </button>
          )}
        </div>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4">
        <StatCard label={heroLabel} value={fmtMoney(totals.revenue)} icon={DollarSign} accent testId="stat-revenue" />
        <StatCard label="Profit" value={fmtMoney(totals.profit)} icon={TrendingUp} testId="stat-profit" />
        <StatCard label="Margin" value={fmtPct(totals.margin_pct)} icon={TrendingUp} testId="stat-margin" />
        <StatCard label="Units" value={fmtInt(totals.units)} icon={Package} testId="stat-units" />
        <StatCard label="Sales" value={fmtInt(totals.sales)} icon={ShoppingCart} testId="stat-sales" />
        <StatCard label="Cost" value={fmtMoney(totals.cost)} icon={DollarSign} testId="stat-cost" />
      </div>

      {/* Chart + top products */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="brutal-card p-4 lg:col-span-2" data-testid="daily-chart">
          <div className="flex items-center justify-between mb-3">
            <div className="font-heading text-xl font-black uppercase">Daily revenue</div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-[#4A4A4A]">{daily.length} day{daily.length !== 1 ? "s" : ""}</div>
          </div>
          {daily.length > 0 ? (
            <div style={{ width: "100%", height: 240 }}>
              <ResponsiveContainer>
                <LineChart data={daily} margin={{ top: 10, right: 16, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="2 2" stroke="#1112" />
                  <XAxis dataKey="date" tick={{ fontFamily: "monospace", fontSize: 10, fill: "#4A4A4A" }} tickFormatter={(s) => s.slice(5)} />
                  <YAxis tick={{ fontFamily: "monospace", fontSize: 10, fill: "#4A4A4A" }} />
                  <Tooltip
                    contentStyle={{ background: "#fff", border: "2px solid #111", boxShadow: "4px 4px 0 #111", fontFamily: "monospace", fontSize: 12 }}
                    formatter={(v, n) => [(n === "revenue" || n === "profit" || n === "cost") ? fmtMoney(v) : fmtInt(v), n]}
                  />
                  <Line type="monotone" dataKey="revenue" stroke="#C2410C" strokeWidth={2.5} dot={{ r: 3 }} />
                  <Line type="monotone" dataKey="profit" stroke="#111" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="font-mono text-xs text-[#4A4A4A] py-12 text-center">No sales in this range yet.</div>
          )}
        </div>

        <div className="brutal-card p-4" data-testid="top-products">
          <div className="font-heading text-xl font-black uppercase mb-3">Top products</div>
          {topProducts.length === 0 ? (
            <div className="font-mono text-xs text-[#4A4A4A] py-8 text-center">Nothing yet.</div>
          ) : (
            <ul className="space-y-2">
              {topProducts.map((p, i) => (
                <li key={p.product_id} className="flex items-center justify-between gap-2">
                  <span className="font-mono text-xs flex items-center gap-2 min-w-0">
                    <span className="brutal-badge !py-0 !px-1 !text-[9px] flex-shrink-0">#{i + 1}</span>
                    <span className="truncate">{p.name}</span>
                  </span>
                  <span className="font-mono text-xs flex items-center gap-2 flex-shrink-0">
                    <span className="text-[#4A4A4A]">{fmtInt(p.units)}u</span>
                    <strong>{fmtMoney(p.revenue)}</strong>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Top sellers + by event */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="brutal-card p-4" data-testid="top-sellers">
          <div className="flex items-center gap-2 mb-3">
            <Users size={16}/>
            <div className="font-heading text-xl font-black uppercase">Top sellers</div>
          </div>
          {topSellers.length === 0 ? (
            <div className="font-mono text-xs text-[#4A4A4A] py-8 text-center">No attributed sales.</div>
          ) : (
            <ul className="space-y-2">
              {topSellers.map((s, i) => (
                <li key={s.user_id} className="flex items-center justify-between gap-2">
                  <span className="font-mono text-xs flex items-center gap-2 min-w-0">
                    <span className="brutal-badge !py-0 !px-1 !text-[9px] flex-shrink-0">#{i + 1}</span>
                    <span className="truncate">{s.name}</span>
                  </span>
                  <span className="font-mono text-xs flex items-center gap-2 flex-shrink-0">
                    <span className="text-[#4A4A4A]">{fmtInt(s.units)}u</span>
                    <strong>{fmtMoney(s.revenue)}</strong>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
        {!eventId && (
          <div className="brutal-card p-4" data-testid="by-event">
            <div className="flex items-center gap-2 mb-3">
              <Trophy size={16}/>
              <div className="font-heading text-xl font-black uppercase">Best events</div>
            </div>
            {byEvent.length === 0 ? (
              <div className="font-mono text-xs text-[#4A4A4A] py-8 text-center">No event-tagged sales.</div>
            ) : (
              <ul className="space-y-2">
                {byEvent.map((e, i) => (
                  <li key={e.event_id} className="flex items-center justify-between gap-2">
                    <span className="font-mono text-xs flex items-center gap-2 min-w-0">
                      <span className="brutal-badge !py-0 !px-1 !text-[9px] flex-shrink-0">#{i + 1}</span>
                      <span className="truncate">{e.name}</span>
                    </span>
                    <span className="font-mono text-xs flex items-center gap-2 flex-shrink-0">
                      <span className="text-[#4A4A4A]">{fmtInt(e.units)}u</span>
                      <strong>{fmtMoney(e.revenue)}</strong>
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
      {loading && <div className="font-mono text-[10px] uppercase tracking-widest text-[#4A4A4A]">Refreshing…</div>}
    </section>
  );
}
